import { parseISO } from 'date-fns';
import { FunctionComponent, memo, MouseEvent, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Event } from '../../../../../services';
import { layoutPath } from '../../../../utils';
import { hasEventStatusBadges } from '../../helpers';
import { EventDateBadge } from '../EventDateBadge';
import { EventStatusBadges } from '../EventStatusBadges';

import classNames from 'classnames';
import classes from './EventItemHeader.module.scss';

interface EventItemHeaderProps {
  event: Event;
  showDateBadge?: boolean;
  onClick?: () => void;
}

export const EventItemHeader: FunctionComponent<EventItemHeaderProps> = memo(
  ({ event, showDateBadge, onClick }) => {
    const navigate = useNavigate();

    const { organisationTags, name, startAt } = event;

    const handleTagClick = useCallback(
      (id: number) => (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        navigate(`${layoutPath('/events')}?tagId=${id}`);
      },
      [navigate]
    );

    const hasTags = organisationTags.length > 0;

    const tags = useMemo(() => {
      if (!hasTags) {
        return null;
      }
      return (
        <div className={classes['event-item-header__tags']}>
          {organisationTags.map(({ id, name: tagName }) => (
            <button
              key={id}
              type={'button'}
              className={classes['event-item-header__tag']}
              onClick={handleTagClick(id)}
            >
              {tagName}
            </button>
          ))}
        </div>
      );
    }, [hasTags, organisationTags, handleTagClick]);

    const title = useMemo(
      () => <div className={classes['event-item-header__title']}>{name}</div>,
      [name]
    );

    const statusBadges = useMemo(() => <EventStatusBadges event={event} />, [event]);

    const dateBadge = useMemo(() => {
      if (!showDateBadge) {
        return null;
      }

      return <EventDateBadge date={parseISO(startAt)} />;
    }, [showDateBadge, startAt]);

    const content = useMemo(() => {
      if (!dateBadge) {
        return (
          <>
            {tags}
            {statusBadges}
            {title}
          </>
        );
      }

      if (hasTags || hasEventStatusBadges(event)) {
        return (
          <>
            <div className={classes['event-item-header__badge-row']}>
              {dateBadge}
              <div className={classes['event-item-header__tags-status']}>
                {tags}
                {statusBadges}
              </div>
            </div>
            {title}
          </>
        );
      }

      return (
        <div className={classes['event-item-header__badge-row']}>
          {dateBadge}
          {title}
        </div>
      );
    }, [dateBadge, hasTags, event, tags, statusBadges, title]);

    return (
      <div
        className={classNames(classes['event-item-header'], {
          [classes['event-item-header--clickable']]: Boolean(onClick),
        })}
        onClick={onClick}
      >
        {content}
      </div>
    );
  }
);
